import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Printer, X, History, Calendar } from "lucide-react";
import Layout from "./layout";
import TemplateDetailModal from "../Modal/TemplateDetailModal";
import EditTemplateModal from "../Modal/editTamplateModal";
import HealthResultModal from "../Modal/HealthResultModal";
import SendHealthResultModal from "../Modal/SendHealthResultModal";
import HealthReportModal from "../Modal/HealthReportModal";
import { useNavigate } from "react-router-dom";
import { healthRecordsAPI, templateAPI, normalRangeAPI, healthRecommendationAPI } from "../services/api";

const PageContainer = styled.div`
  background-color: #FFFFFF;
  border-radius: 0.5rem;
  border: 1px solid #E5E7EB;
  padding: 2rem;
  margin-bottom: 2rem;
`;

const PageTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 400;
  color: #111827;
  margin-bottom: 2rem;
`;

const FilterSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr 1fr auto auto;
  grid-template-rows: repeat(2, auto);
  gap: 1rem;
  margin-bottom: 2rem;
  align-items: end;
`;

const GridItem = styled.div`
  grid-column: ${props => props.column};
  grid-row: ${props => props.row};
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  display: block;
  margin-bottom: 0.5rem;
`;

const DateInputContainer = styled.div`
  position: relative;
`;

const DateInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  padding-right: 2.5rem;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  color: #374151;

  &:focus {
    outline: none;
    border-color: #00B5B8;
    box-shadow: 0 0 0 3px rgba(0, 181, 184, 0.1);
  }

  &::-webkit-calendar-picker-indicator {
    opacity: 0;
    position: absolute;
    right: 0;
    width: 100%;
    height: 100%;
    cursor: pointer;
  }
`;

const CalendarIcon = styled.div`
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #9CA3AF;
  pointer-events: none;
`;

const TextInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  
  &::placeholder {
    color: #9CA3AF;
  }
  
  &:focus {
    outline: none;
    border-color: #00B5B8;
    box-shadow: 0 0 0 3px rgba(0, 181, 184, 0.1);
  }
`;

const SelectInput = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  background-color: white;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: #00B5B8;
    box-shadow: 0 0 0 3px rgba(0, 181, 184, 0.1);
  }
`;

const SearchButton = styled.button`
  background-color: #00B5B8;
  color: white;
  border: none;
  border-radius: 0.375rem;
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  width: 100%;
  height: 46px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: #0891B2;
  }
`;

const PrintButton = styled.button`
  background-color: #F9FAFB;
  color: #374151;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  width: 100%;
  height: 46px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;

  &:hover {
    background-color: #F3F4F6;
  }
`;

const TemplateBadge = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  background-color: transparent;
  border: 1px solid #00B5B8;
  color: #00B5B8;
  border-radius: 0px;
  padding: 0.25rem 0.5rem;
  margin: 0.125rem;
  font-size: 0.75rem;
  height: 30px;
  width: auto;
  position: relative;
  cursor: pointer;
`;

const TemplateBadgeText = styled.span`
  margin-right: 0.5rem;
`;

const RemoveButton = styled.button`
  background: white;
  border: 1px solid #00B5B8;
  cursor: pointer;
  color: #00B5B8;
  padding: 0;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  position: absolute;
  top: -8px;
  right: -8px;
`;

const ViewDataButton = styled.button`
  background-color: #FBFFFF;
  color: #9c9e9eff;
  border: 1px solid #CFD3D4;
  border-radius: 0.375rem;
  padding: 0.5rem 1rem;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  margin-bottom: 0.5rem;
  width: auto;
  white-space: nowrap;
  
  &:hover {
    background-color: #F3F4F6;
  }
`;

const StatusText = styled.div`
  font-size: 0.75rem;
  color: #374151;
  margin-bottom: 0.5rem;
  text-align: center;
`;

const StatusBadge = styled.div`
  display: inline-block;
  font-size: 14px;
  font-weight: 400;
  margin-bottom: 0.5rem;
  text-align: center;
  width: 100%;
  text-decoration: underline;
  background: none;
  border: none;
  color: ${props => 
    props.status === 'รอการอนุมัติ' ? '#FF0404' :
    props.status === 'อนุมัติและจัดส่งผลการตรวจสุขภาพแล้ว' ? '#00B5B8' :
    props.status === 'มีการแก้ไขรอการอนุมัติอีกครั้ง' ? '#FF0404' : '#6F6F6F'
  };
`;

const ActionPrintButton = styled.button`
  background: none;
  color: ${props => props.disabled ? '#9CA3AF' : '#374151'};
  border: none;
  padding: 0.5rem 1rem;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  width: 100%;
  opacity: ${props => props.disabled ? 0.5 : 1};

  &:hover {
    color: ${props => props.disabled ? '#9CA3AF' : '#00B5B8'};
  }
`;

const EditButton = styled.button`
  background-color: ${props => props.disabled ? '#F3F4F6' : '#FFFFFF'};
  color: ${props => props.disabled ? '#9CA3AF' : '#374151'};
  border: 1px solid ${props => props.disabled ? '#E5E7EB' : '#CFD3D4'};
  border-radius: 0.375rem;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  margin: 0.125rem;

  &:hover {
    background-color: ${props => props.disabled ? '#F3F4F6' : '#F9FAFB'};
  }
`;

const ViewHiddenButton = styled.button`
  background-color: #FFF7ED;
  color: #EA580C;
  border: 1px solid #FDBA74;
  border-radius: 0.375rem;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: #FFEDD5;
    border-color: #FB923C;
  }
`;

const SendResultButton = styled.button`
  background-color: #00B5B8;
  color: #FFFFFF;
  border: 1px solid #00B5B8;
  border-radius: 0.375rem;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  margin: 0.125rem;

  &:hover {
    background-color: #0891B2;
    border-color: #0891B2;
  }
`;

const HistoryButton = styled.button`
  background: none;
  border: none;
  color: #6F6F6F;
  font-size: 14px;
  font-weight: 400;
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  text-decoration: underline;

  &:hover {
    color: #6F6F6F;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const TableContainer = styled.div`
  overflow-x: auto;
  margin-top: 2rem;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background-color: #F9FAFB;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #eee;

  &:hover {
    background-color: #F9FAFB;
  }
`;

const TableHeaderCell = styled.th`
  padding: 1rem;
  font-size: 14px;
  font-weight: 400;
  color: #A3A3A3;
  text-align: center;
`;

const TableHeaderCell2 = styled.th`
  padding: 1rem;
  font-size: 14px;
  font-weight: 400;
  color: #A3A3A3;
  text-align: center;
`;

const TableCell = styled.td`
  padding: 1rem;
  font-size: 14px;
  color: #6F6F6F;
  text-align: center;
  vertical-align: top;
`;

const HealthBook = () => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [searchCompany, setSearchCompany] = useState("");
  const [searchText, setSearchText] = useState("");
  const [status, setStatus] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isHealthResultModalOpen, setIsHealthResultModalOpen] = useState(false);
  const [isSendHealthResultModalOpen, setIsSendHealthResultModalOpen] = useState(false);
  const [isHealthReportModalOpen, setIsHealthReportModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [isHiddenTemplatesModalOpen, setIsHiddenTemplatesModalOpen] = useState(false);
  const [hiddenTemplates, setHiddenTemplates] = useState([]);
  const [selectedRecordId, setSelectedRecordId] = useState(null);
  const navigate = useNavigate();


  const handleSearch = () => {
    // ถ้าไม่มีการกรองอะไรเลย แสดงข้อมูลทั้งหมด (Auto Reset)
    if (!startDate && !endDate && !searchText && !searchCompany && !status) {
      setHealthRecords(allHealthRecords);
      return;
    }

    let filtered = [...allHealthRecords];

    // 1. Filter ตามวันที่เริ่ม
    if (startDate) {
      filtered = filtered.filter(record => {
        if (!record.date || record.date === '-') return false;
        return record.date >= startDate;
      });
    }

    // 2. Filter ตามวันที่สิ้นสุด
    if (endDate) {
      filtered = filtered.filter(record => {
        if (!record.date || record.date === '-') return false;
        return record.date <= endDate;
      });
    }

    // 3. Filter ตามชื่อ-สกุล หรือ เลขบัตร (ค้นหาใน patientName และ nid)
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(record => {
        const nameMatch = record.patientName.toLowerCase().includes(searchLower);
        const hnMatch = record.hn.toLowerCase().includes(searchLower);
        return nameMatch || hnMatch;
      });
    }

    // 4. Filter ตามบริษัท (ถ้ามีข้อมูล company ใน healthData)
    if (searchCompany) {
      const companyLower = searchCompany.toLowerCase();
      filtered = filtered.filter(record => {
        // สมมติว่ามีข้อมูล company ใน record
        const company = record.company || '';
        return company.toLowerCase().includes(companyLower);
      });
    }

    // 5. Filter ตามสถานะ
    if (status) {
      // แปลงข้อความสถานะเป็น approval_status enum
      let statusEnum = 'pending';
      if (status === 'อนุมัติและจัดส่งผลการตรวจสุขภาพแล้ว') {
        statusEnum = 'approved';
      } else if (status === 'มีการแก้ไขรอการอนุมัติอีกครั้ง') {
        statusEnum = 'revised';
      }
      filtered = filtered.filter(record => record.approvalStatus === statusEnum);
    }

    // อัพเดทข้อมูลที่แสดง
    setHealthRecords(filtered);
  };

  const handlePrint = (recordId) => {
    const record = healthRecords.find(r => r.id === recordId);
    if (record) {
      setSelectedRecord(record);
      setIsHealthReportModalOpen(true);
    }
  };

  const handleViewData = (recordId) => {
    const record = healthRecords.find(r => r.id === recordId);
    if (record) {
      setSelectedRecord(record);
      setIsHealthReportModalOpen(true);
    }
  };

  const handleRemoveTemplate = async (recordId, templateIndex) => {
    const record = healthRecords.find(r => r.id === recordId);
    if (!record) return;

    const templateName = record.templates[templateIndex];
    const matchedTemplate = record.matchedTemplates?.find(t => t.template_name === templateName);

    if (!matchedTemplate || !matchedTemplate.template_id) {
      console.error('Cannot find template ID for:', templateName);
      return;
    }

    try {
      // เรียก API เพื่อซ่อน template (persist ลง database)
      await healthRecordsAPI.hideTemplate(
        recordId,
        matchedTemplate.template_id,
        templateName
      );

      // อัพเดท local state หลังจาก API สำเร็จ
      const updatedRecords = healthRecords.map(r => {
        if (r.id === recordId) {
          const updatedTemplates = r.templates.filter((_, index) => index !== templateIndex);
          const updatedMatchedTemplates = r.matchedTemplates?.filter((_, index) => index !== templateIndex);
          const updatedTags = [];
          updatedMatchedTemplates?.forEach(t => {
            updatedTags.push(...(t.tags || []));
          });

          return {
            ...r,
            templates: updatedTemplates,
            matchedTemplates: updatedMatchedTemplates,
            tags: [...new Set(updatedTags)]
          };
        }
        return r;
      });

      setHealthRecords(updatedRecords);
      setAllHealthRecords(updatedRecords);

    } catch (error) {
      console.error('❌ Error hiding template:', error);
    }
  };

  const handleViewHiddenTemplates = async (recordId) => {
    try {
      setSelectedRecordId(recordId);
      const response = await healthRecordsAPI.getHiddenTemplates(recordId);

      if (response.status === 'success' && Array.isArray(response.data)) {
        setHiddenTemplates(response.data);
        setIsHiddenTemplatesModalOpen(true);
      } else {
        console.error('❌ ไม่สามารถดึงข้อมูล Templates ที่ซ่อนได้');
      }
    } catch (error) {
      console.error('❌ Error fetching hidden templates:', error);
    }
  };

  const handleRestoreTemplate = async (templateId) => {
    if (!selectedRecordId) return;

    try {
      await healthRecordsAPI.unhideTemplate(selectedRecordId, templateId);

      // รีโหลดข้อมูล health records
      await fetchHealthRecords();

      // รีโหลดรายการ hidden templates
      const response = await healthRecordsAPI.getHiddenTemplates(selectedRecordId);
      if (response.status === 'success' && Array.isArray(response.data)) {
        setHiddenTemplates(response.data);

        // ถ้าไม่มี hidden templates แล้ว ปิด modal
        if (response.data.length === 0) {
          setIsHiddenTemplatesModalOpen(false);
        }
      }

    } catch (error) {
      console.error('❌ Error restoring template:', error);
    }
  };

  const handleEdit = (recordId) => {
    const record = healthRecords.find(r => r.id === recordId);
    if (record) {
      setSelectedRecord(record);
      setSelectedRecordId(recordId);
      setIsEditModalOpen(true);
    }
  };

  const handleSendResult = (recordId) => {
    const record = healthRecords.find(r => r.id === recordId);
    if (record) {
      setSelectedRecord(record);
      if (record.isApproved) {
        // ถ้าเป็นการส่งใหม่ แสดง SendHealthResultModal
        setIsSendHealthResultModalOpen(true);
      } else {
        // ถ้าเป็นการส่งครั้งแรก แสดง HealthResultModal
        setIsHealthResultModalOpen(true);
      }
    }
  };

  const handleTemplateClick = async (templateName, matchedTemplateData = null) => {
    try {
      let template = null;

      // ถ้ามี matchedTemplateData ใช้เลย แต่ต้อง fetch รายละเอียดเพิ่ม
      if (matchedTemplateData && matchedTemplateData.template_id) {
        const response = await templateAPI.getById(matchedTemplateData.template_id);
        if (response.status === 'success' && response.data) {
          template = response.data;
        }
      } else {
        // ถ้าไม่มี ให้ค้นหาจาก template name
        const response = await templateAPI.getAll();
        if (response.status === 'success' && response.data) {
          template = response.data.find(t => t.template_name === templateName);
        }
      }

      if (template) {
        // แปลงข้อมูลให้ตรงกับ format ที่ TemplateDetailModal ต้องการ
        const formattedTemplate = {
          templateName: template.template_name,
          createdBy: template.created_by,
          tags: template.tags || [],
          gender: template.gender === 'male' ? 'ชาย' : template.gender === 'female' ? 'หญิง' : 'ทั้งสองเพศ',
          ageRange: `${template.age_min}-${template.age_max}`,
          parameters: template.parameters?.map(p => p.parameter_name) || [],
          normalValues: template.parameters?.map(p => p.normal_range_text) || [],
          setValues: template.parameters?.map(p => `${p.custom_min}-${p.custom_max}`) || []
        };

        setSelectedTemplate(formattedTemplate);
        setIsModalOpen(true);
      } else {
        console.warn('Template not found:', templateName);
      }
    } catch (error) {
      console.error('Error loading template:', error);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTemplate(null);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    // รีเฟรชข้อมูลหลังปิด modal เพื่อให้แสดง tags ที่อัพเดทแล้ว
    fetchHealthRecords();
  };

  const handleCloseHealthResultModal = () => {
    setIsHealthResultModalOpen(false);
    setSelectedRecord(null);
  };

  const handleApprove = async (recordId) => {
    // Reload ข้อมูลใหม่จาก database เพื่อให้ได้ approval_status ที่ถูกต้อง
    await fetchHealthRecords();
  };

  const handleViewHistory = (recordId) => {
    const record = healthRecords.find(r => r.id === recordId);
    if (record) {
      navigate('/health-records-history', { state: { selectedRecord: record } });
    }
  };

  const handleCloseSendHealthResultModal = () => {
    setIsSendHealthResultModalOpen(false);
    setSelectedRecord(null);
  };

  const handleConfirmSendAgain = async () => {
    // เปลี่ยนสถานะเป็น 'revised' โดยการบันทึก draft ใหม่
    // Backend จะตรวจสอบว่าถ้าเคย approved แล้วจะเปลี่ยนเป็น revised อัตโนมัติ
    if (selectedRecord) {
      try {
        console.log('🔄 DEBUG - Sending request to change status to revised');
        console.log('🔄 DEBUG - Record ID:', selectedRecord.id);
        console.log('🔄 DEBUG - Current approval status:', selectedRecord.approvalStatus);

        // โหลดข้อมูล recommendations ปัจจุบัน
        const response = await healthRecommendationAPI.getDraft(selectedRecord.id);
        console.log('🔄 DEBUG - getDraft response:', response);

        if (response.status === 'success' && response.data) {
          // บันทึก draft เพื่อให้ backend เปลี่ยนสถานะเป็น revised
          const saveResponse = await healthRecommendationAPI.saveDraft(
            selectedRecord.id,
            {
              doctor_advice: response.data.doctor_advice || '',
              nutrition_advice: response.data.nutrition_advice || '',
              exercise_advice: response.data.exercise_advice || '',
              sleep_advice: response.data.sleep_advice || ''
            },
            'admin'
          );
          console.log('🔄 DEBUG - saveDraft response:', saveResponse);

          // Reload ข้อมูลใหม่เพื่อแสดงสถานะที่อัพเดท
          console.log('🔄 DEBUG - Reloading health records...');
          await fetchHealthRecords();
          console.log('🔄 DEBUG - Health records reloaded');
        }
      } catch (error) {
        console.error('❌ Error changing status to revised:', error);
      }
    }
    setIsSendHealthResultModalOpen(false);
    setIsHealthResultModalOpen(true);
  };

  const handleCloseHealthReportModal = () => {
    setIsHealthReportModalOpen(false);
    setSelectedRecord(null);
  };

  // State สำหรับเก็บข้อมูลจาก API
  const [healthRecords, setHealthRecords] = useState([]);
  const [allHealthRecords, setAllHealthRecords] = useState([]); // เก็บข้อมูลทั้งหมดไว้สำหรับ filter
  const [loading, setLoading] = useState(false);

  // ดึงข้อมูลจาก Database เมื่อ component โหลด
  useEffect(() => {
    fetchHealthRecords();
  }, []);

  // Auto-refresh เมื่อกลับมาที่หน้านี้ (เช่น หลังจากไปปิด template แล้วกลับมา)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // เมื่อ tab กลับมา active ให้ reload ข้อมูล
        fetchHealthRecords();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const fetchHealthRecords = async () => {
    try {
      setLoading(true);
      const response = await healthRecordsAPI.getAll();

      if (response.status === 'success' && Array.isArray(response.data)) {
        // แปลงข้อมูลจาก Database ให้ตรงกับรูปแบบที่ UI ใช้
        const formattedData = await Promise.all(response.data.map(async (record, index) => {
          const healthData = record.health_data || {};

          // ดึงข้อมูล Lab Results (อยู่ใน health_data.health_data[0])
          const labData = Array.isArray(healthData.health_data) && healthData.health_data.length > 0
            ? healthData.health_data[0]
            : {};

          // ดึงอายุและเพศสำหรับ fallback ค่าปกติ
          const age = labData['age'] || 0;
          const sex = labData['sex'] === 'ชาย' ? 'M' : 'F';

          // แปลงข้อมูล Lab Results เป็น parameters สำหรับแสดงในตาราง
          const parameters = [];
          const testsNeedingNormalRange = []; // เก็บรายการที่ต้องดึงค่าปกติจาก medium.json

          // CBC - Complete Blood Count
          if (labData['cbc rbc result']) {
            const param = {
              name: 'RBC',
              result: labData['cbc rbc result'],
              normal: labData['cbc rbc normal value']
            };
            parameters.push(param);
            if (!param.normal) {
              testsNeedingNormalRange.push({ param, name: 'RBC', sex, age });
            }
          }

          if (labData['cbc wbc result']) {
            const param = {
              name: 'WBC',
              result: labData['cbc wbc result'],
              normal: labData['cbc wbc normal_value']
            };
            parameters.push(param);
            if (!param.normal) {
              testsNeedingNormalRange.push({ param, name: 'WBC', sex, age });
            }
          }

          if (labData['cbc hb result']) {
            const param = {
              name: 'Hemoglobin',
              result: labData['cbc hb result'],
              normal: labData['cbc hb normal value']
            };
            parameters.push(param);
            if (!param.normal) {
              testsNeedingNormalRange.push({ param, name: 'Hemoglobin', sex, age });
            }
          }

          if (labData['cbc hct result']) {
            const param = {
              name: 'Hematocrit',
              result: labData['cbc hct result'],
              normal: labData['cbc hct normal value']
            };
            parameters.push(param);
            if (!param.normal) {
              testsNeedingNormalRange.push({ param, name: 'Hematocrit', sex, age });
            }
          }

          if (labData['cbc mcv result']) {
            const param = {
              name: 'MCV',
              result: labData['cbc mcv result'],
              normal: labData['cbc mcv normal value']
            };
            parameters.push(param);
            if (!param.normal) {
              testsNeedingNormalRange.push({ param, name: 'MCV', sex, age });
            }
          }

          if (labData['cbc mch result']) {
            const param = {
              name: 'MCH',
              result: labData['cbc mch result'],
              normal: labData['cbc mch normal value']
            };
            parameters.push(param);
            if (!param.normal) {
              testsNeedingNormalRange.push({ param, name: 'MCH', sex, age });
            }
          }

          if (labData['cbc mchc result']) {
            const param = {
              name: 'MCHC',
              result: labData['cbc mchc result'],
              normal: labData['cbc mchc normal value']
            };
            parameters.push(param);
            if (!param.normal) {
              testsNeedingNormalRange.push({ param, name: 'MCHC', sex, age });
            }
          }

          if (labData['cbc plt count result']) {
            const param = {
              name: 'Platelets',
              result: labData['cbc plt count result'],
              normal: labData['cbc plt count normal value']
            };
            parameters.push(param);
            if (!param.normal) {
              testsNeedingNormalRange.push({ param, name: 'Platelets', sex, age });
            }
          }

          if (labData['cbc neutrophile result']) {
            const param = {
              name: 'Neutrophile',
              result: labData['cbc neutrophile result'],
              normal: labData['cbc neutrophile normal value']
            };
            parameters.push(param);
            if (!param.normal) {
              testsNeedingNormalRange.push({ param, name: 'Neutrophile', sex, age });
            }
          }

          if (labData['cbc lymphocyte result']) {
            const param = {
              name: 'Lymphocyte',
              result: labData['cbc lymphocyte result'],
              normal: labData['cbc lymphocyte normal value']
            };
            parameters.push(param);
            if (!param.normal) {
              testsNeedingNormalRange.push({ param, name: 'Lymphocyte', sex, age });
            }
          }

          if (labData['cbc monocyte result']) {
            const param = {
              name: 'Monocyte',
              result: labData['cbc monocyte result'],
              normal: labData['cbc monocyte normal value']
            };
            parameters.push(param);
            if (!param.normal) {
              testsNeedingNormalRange.push({ param, name: 'Monocyte', sex, age });
            }
          }

          // Kidney Function Tests
          if (labData['creatinine creatinine result']) {
            const param = {
              name: 'Creatinine',
              result: labData['creatinine creatinine result'],
              normal: labData['creatinine creatinine normal value']
            };
            parameters.push(param);
            if (!param.normal) {
              testsNeedingNormalRange.push({ param, name: 'Creatinine', sex, age });
            }
          }

          // BUN (Blood Urea Nitrogen)
          if (labData['bun result']) {
            const param = {
              name: 'BUN, Urea',
              result: labData['bun result'],
              normal: labData['bun normal value']
            };
            parameters.push(param);
            if (!param.normal) {
              testsNeedingNormalRange.push({ param, name: 'BUN, Urea', sex, age });
            }
          }

          if (labData['creatinine egfr result']) {
            const param = {
              name: 'eGFR',
              result: labData['creatinine egfr result'],
              normal: labData['creatinine egfr normal value']
            };
            parameters.push(param);
            if (!param.normal) {
              testsNeedingNormalRange.push({ param, name: 'eGFR', sex, age });
            }
          }

          if (labData['uric acid result']) {
            const param = {
              name: 'Uric Acid',
              result: labData['uric acid result'],
              normal: labData['uric acid normal value']
            };
            parameters.push(param);
            if (!param.normal) {
              testsNeedingNormalRange.push({ param, name: 'Uric Acid', sex, age });
            }
          }

          // Diabetes Tests
          if (labData['glucose result']) {
            const param = {
              name: 'Glucose',
              result: labData['glucose result'],
              normal: labData['glucose normal value']
            };
            parameters.push(param);
            if (!param.normal) {
              testsNeedingNormalRange.push({ param, name: 'Glucose', sex, age });
            }
          }

          if (labData['hba1c hb a1c result']) {
            const param = {
              name: 'HbA1C',
              result: labData['hba1c hb a1c result'],
              normal: labData['hba1c hb a1c normal value']
            };
            parameters.push(param);
            if (!param.normal) {
              testsNeedingNormalRange.push({ param, name: 'HbA1C', sex, age });
            }
          }

          // Liver Function Tests
          if (labData['sgpt result']) {
            const param = {
              name: 'SGPT (ALT)',
              result: labData['sgpt result'],
              normal: labData['sgpt normal value']
            };
            parameters.push(param);
            if (!param.normal) {
              testsNeedingNormalRange.push({ param, name: 'SGPT (ALT)', sex, age });
            }
          }

          if (labData['liver function test sgot ast result']) {
            const param = {
              name: 'SGOT (AST)',
              result: labData['liver function test sgot ast result'],
              normal: labData['liver function test sgot ast normal value']
            };
            parameters.push(param);
            if (!param.normal) {
              testsNeedingNormalRange.push({ param, name: 'SGOT (AST)', sex, age });
            }
          }

          if (labData['liver function test alkaline phosphatase result']) {
            const param = {
              name: 'ALP (Alkaline Phosphatase)',
              result: labData['liver function test alkaline phosphatase result'],
              normal: labData['liver function test alkaline phosphatase normal value']
            };
            parameters.push(param);
            if (!param.normal) {
              testsNeedingNormalRange.push({ param, name: 'ALP', sex, age });
            }
          }

          if (labData['liver function test albumin result']) {
            const param = {
              name: 'Albumin',
              result: labData['liver function test albumin result'],
              normal: labData['liver function test albumin normal value']
            };
            parameters.push(param);
            if (!param.normal) {
              testsNeedingNormalRange.push({ param, name: 'Albumin', sex, age });
            }
          }

          if (labData['liver function test total protein result']) {
            const param = {
              name: 'Total Protein',
              result: labData['liver function test total protein result'],
              normal: labData['liver function test total protein normal value']
            };
            parameters.push(param);
            if (!param.normal) {
              testsNeedingNormalRange.push({ param, name: 'Total Protein', sex, age });
            }
          }

          if (labData['liver function test globulin result']) {
            const param = {
              name: 'Globulin',
              result: labData['liver function test globulin result'],
              normal: labData['liver function test globulin normal value']
            };
            parameters.push(param);
            if (!param.normal) {
              testsNeedingNormalRange.push({ param, name: 'Globulin', sex, age });
            }
          }

          if (labData['liver function test bilirubin total result']) {
            const param = {
              name: 'Total Bilirubin',
              result: labData['liver function test bilirubin total result'],
              normal: labData['liver function test bilirubin total normal value']
            };
            parameters.push(param);
            if (!param.normal) {
              testsNeedingNormalRange.push({ param, name: 'Total Bilirubin', sex, age });
            }
          }

          if (labData['ggt result']) {
            const param = {
              name: 'GGT',
              result: labData['ggt result'],
              normal: labData['ggt normal value']
            };
            parameters.push(param);
            if (!param.normal) {
              testsNeedingNormalRange.push({ param, name: 'GGT', sex, age });
            }
          }

          // Lipid Profile
          if (labData['hdl result']) {
            const param = {
              name: 'HDL',
              result: labData['hdl result'],
              normal: labData['hdl normal value']
            };
            parameters.push(param);
            if (!param.normal) {
              testsNeedingNormalRange.push({ param, name: 'HDL', sex, age });
            }
          }

          if (labData['ldl result']) {
            const param = {
              name: 'LDL',
              result: labData['ldl result'],
              normal: labData['ldl normal value']
            };
            parameters.push(param);
            if (!param.normal) {
              testsNeedingNormalRange.push({ param, name: 'LDL', sex, age });
            }
          }

          if (labData['total cholesterol result']) {
            const param = {
              name: 'คอเลสเตอรอลรวม',
              result: labData['total cholesterol result'],
              normal: labData['total cholesterol normal value']
            };
            parameters.push(param);
            if (!param.normal) {
              testsNeedingNormalRange.push({ param, name: 'คอเลสเตอรอลรวม', sex, age });
            }
          }

          if (labData['triglyceride result']) {
            const param = {
              name: 'ไตรกลีเซอไรด์',
              result: labData['triglyceride result'],
              normal: labData['triglyceride normal value']
            };
            parameters.push(param);
            if (!param.normal) {
              testsNeedingNormalRange.push({ param, name: 'ไตรกลีเซอไรด์', sex, age });
            }
          }

          // Urinalysis
          if (labData['urinalysis sugar result']) {
            const param = {
              name: 'Glucose',
              result: labData['urinalysis sugar result'],
              normal: labData['urinalysis sugar normal value']
            };
            parameters.push(param);
            if (!param.normal) {
              testsNeedingNormalRange.push({ param, name: 'Glucose', sex, age });
            }
          }

          if (labData['urinalysis protein result']) {
            const param = {
              name: 'Protein',
              result: labData['urinalysis protein result'],
              normal: labData['urinalysis protein normal value']
            };
            parameters.push(param);
            if (!param.normal) {
              testsNeedingNormalRange.push({ param, name: 'Protein', sex, age });
            }
          }

          // ถ้ามี parameters ที่ขาดค่าปกติ ให้ดึงจาก medium.json
          if (testsNeedingNormalRange.length > 0) {
            try {
              const testsForAPI = testsNeedingNormalRange.map(t => ({
                name: t.name,
                sex: t.sex,
                age: t.age
              }));

              const normalRangeResponse = await normalRangeAPI.getBatch(testsForAPI);

              if (normalRangeResponse.status === 'success' && normalRangeResponse.data) {
                // อัพเดทค่าปกติที่ดึงมาได้
                normalRangeResponse.data.forEach(result => {
                  if (result.normal_range && !result.error) {
                    const test = testsNeedingNormalRange.find(t => t.name === result.test_name);
                    if (test && test.param) {
                      test.param.normal = result.normal_range;
                    }
                  }
                });
              }
            } catch (error) {
              console.error('Error fetching normal ranges from medium.json:', error);
            }
          }

          // ใส่ค่า default '-' สำหรับ parameters ที่ยังไม่มีค่าปกติ
          parameters.forEach(param => {
            if (!param.normal) {
              param.normal = '-';
            }
          });

          // ถ้าไม่มี parameters จาก Lab ให้ใช้จาก healthData.parameters (ถ้ามี)
          if (parameters.length === 0 && healthData.parameters) {
            parameters.push(...(healthData.parameters || []).map(param => ({
              name: param.name || '',
              result: param.value || '-',
              normal: param.normal || '-'
            })));
          }

          // แปลงวันที่จาก YYYY-MM-DD เป็น DD-MM-YYYY
          const formatDate = (dateString) => {
            if (!dateString || dateString === '-') return '-';
            const parts = dateString.split('-');
            if (parts.length === 3) {
              return `${parts[2]}-${parts[1]}-${parts[0]}`;
            }
            return dateString;
          };

          // โหลด approval_status และคำแนะนำจาก health_recommendations
          let approvalStatus = 'pending';
          let approvedAt = null;
          let nutritionAdvice = '';
          let exerciseAdvice = '';
          let sleepAdvice = '';
          let doctorAdvice = '';

          try {
            const recResponse = await healthRecommendationAPI.getDraft(record.id);
            if (recResponse.status === 'success' && recResponse.data) {
              approvalStatus = recResponse.data.approval_status || 'pending';
              approvedAt = recResponse.data.approved_at;
              nutritionAdvice = recResponse.data.nutrition_advice || '';
              exerciseAdvice = recResponse.data.exercise_advice || '';
              sleepAdvice = recResponse.data.sleep_advice || '';
              doctorAdvice = recResponse.data.doctor_advice || '';
            }
          } catch (err) {
            console.log('No recommendations yet for record', record.id);
          }

          return {
            id: record.id,
            no: index + 1,
            date: formatDate(record.visit_date),
            patientName: `${record.prename || ''}${record.firstname || ''} ${record.lastname || ''}`.trim() || '-',
            gender: labData.sex || '-',
            age: labData.age || '-',
            diseases: healthData.diseases || 'ไม่มี',
            foodAllergies: labData['food allergies'] || '-',
            hn: record.nid || '-',
            parameters: parameters,
            templates: healthData.templates || [],
            matchedTemplates: healthData.matched_templates || [],
            tags: healthData.tags || [],
            approvalStatus: approvalStatus,
            approvedAt: approvedAt,
            isApproved: approvalStatus === 'approved',
            // ข้อมูลเพิ่มเติมสำหรับ PDF
            labData: labData, // ข้อมูล lab ทั้งหมด
            weight: labData['body weight'] || '-',
            height: labData['height'] || '-',
            bmi: labData['bmi'] || '-',
            bloodPressure: (labData['bps'] && labData['bpd']) ? `${labData['bps']}/${labData['bpd']}` : '-',
            pulse: labData['pulse'] || '-',
            doctorNote: doctorAdvice || labData['doctor note'] || '-',
            // คำแนะนำจาก AI
            nutritionAdvice: nutritionAdvice,
            exerciseAdvice: exerciseAdvice,
            sleepAdvice: sleepAdvice
          };
        }));

        setHealthRecords(formattedData);
        setAllHealthRecords(formattedData); // เก็บข้อมูลทั้งหมดไว้สำหรับ filter
      } else {
        // ถ้าไม่มีข้อมูล ให้แสดงเป็น array ว่าง
        setHealthRecords([]);
        setAllHealthRecords([]);
      }
    } catch (err) {
      console.error('Error fetching health records:', err);
      // ถ้า error ให้แสดงเป็น array ว่าง
      setHealthRecords([]);
      setAllHealthRecords([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <PageContainer>
        <PageTitle>สมุดสุขภาพ</PageTitle>

        <FilterSection>
          {/* แถวที่ 1 - Labels */}
          <GridItem column="1" row="1">
            <Label>วันที่ตรวจเริ่ม</Label>
          </GridItem>
          <GridItem column="2" row="1">
            <Label>ถึง</Label>
          </GridItem>
          <GridItem column="3" row="1">
            <Label>ค้นหาชื่อ-สกุล, เลขบัตร</Label>
          </GridItem>
          <GridItem column="4" row="1">
            <Label>บริษัท</Label>
          </GridItem>
          <GridItem column="5" row="1">
            <Label>สถานะ</Label>
          </GridItem>

          {/* แถวที่ 2 - Input Fields และ Buttons */}
          <GridItem column="1" row="2">
            <DateInputContainer>
              <DateInput
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
              <CalendarIcon>
                <Calendar size={16} />
              </CalendarIcon>
            </DateInputContainer>
          </GridItem>

          <GridItem column="2" row="2">
            <DateInputContainer>
              <DateInput
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
              <CalendarIcon>
                <Calendar size={16} />
              </CalendarIcon>
            </DateInputContainer>
          </GridItem>

          <GridItem column="3" row="2">
            <TextInput
              type="text"
              placeholder="พิมพ์ชื่อค้นหา"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </GridItem>

          <GridItem column="4" row="2">
            <TextInput
              type="text"
              placeholder="ค้นหาบริษัท"
              value={searchCompany}
              onChange={(e) => setSearchCompany(e.target.value)}
            />
          </GridItem>

          <GridItem column="5" row="2">
            <SelectInput
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="">เลือกสถานะ</option>
              <option value="รอการอนุมัติ">รอการอนุมัติ</option>
              <option value="อนุมัติและจัดส่งผลการตรวจสุขภาพแล้ว">อนุมัติและจัดส่งผลการตรวจสุขภาพแล้ว</option>
              <option value="มีการแก้ไขรอการอนุมัติอีกครั้ง">มีการแก้ไขรอการอนุมัติอีกครั้ง</option>
            </SelectInput>
          </GridItem>

          <GridItem column="6" row="2">
            <SearchButton onClick={handleSearch}>
              ค้นหา
            </SearchButton>
          </GridItem>

          <GridItem column="7" row="2">
            <PrintButton onClick={handlePrint}>
              <Printer size={16} />
              Print
            </PrintButton>
          </GridItem>
        </FilterSection>

        <TableContainer>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHeaderCell rowSpan="2" style={{ width: '50px' }}>ที่</TableHeaderCell>
                <TableHeaderCell rowSpan="2" style={{ width: '100px' }}>วันที่ตรวจ</TableHeaderCell>
                <TableHeaderCell rowSpan="2" style={{ width: '180px' }}>ชื่อผู้รับการตรวจ/เพศ/อายุ/โรคประจำตัว</TableHeaderCell>
                <TableHeaderCell rowSpan="2" style={{ width: '120px' }}>HN</TableHeaderCell>
                <TableHeaderCell colSpan="3">ผลการตรวจสุขภาพ</TableHeaderCell>
                <TableHeaderCell rowSpan="2" style={{ width: '200px' }}>การแปลผลสุขภาพที่กำหนดไว้เบื้องต้น/ Doctor Note</TableHeaderCell>
                <TableHeaderCell rowSpan="2" style={{ width: '150px' }}>การจัดการข้อมูล</TableHeaderCell>
              </TableRow>
              <TableRow>
                <TableHeaderCell2 style={{ width: '150px' }}>พารามิเตอร์</TableHeaderCell2>
                <TableHeaderCell2 style={{ width: '120px' }}>ค่าที่ตรวจวัดได้</TableHeaderCell2>
                <TableHeaderCell2 style={{ width: '150px' }}>ค่าปกติ</TableHeaderCell2>
              </TableRow>
            </TableHeader>
            <tbody>
              {healthRecords.map((record) => (
                <TableRow key={record.id}>
                  <TableCell>{record.no}</TableCell>
                  <TableCell>{record.date}</TableCell>
                  <TableCell style={{ textAlign: 'left' }}>
                    <div><span style={{ fontWeight: '700' }}>ชื่อ:</span> <span style={{ fontWeight: '400' }}>{record.patientName}</span></div>
                    <div><span style={{ fontWeight: '700' }}>เพศ:</span> {record.gender}</div>
                    <div><span style={{ fontWeight: '700' }}>อายุ:</span> {record.age} ปี</div>
                    <div><span style={{ fontWeight: '700' }}>โรคประจำตัว:</span></div>
                    <div style={{ fontWeight: '400' }}>{record.diseases}</div>
                  </TableCell>
                  <TableCell>{record.hn}</TableCell>
                  <TableCell>
                    {record.parameters.map((param, index) => (
                      <div key={index} style={{ textAlign: 'left', marginBottom: '0.25rem' }}>
                        {param.name}
                      </div>
                    ))}
                  </TableCell>
                  <TableCell>
                    {record.parameters.map((param, index) => (
                      <div key={index} style={{ marginBottom: '0.25rem' }}>
                        {param.result}
                      </div>
                    ))}
                  </TableCell>
                  <TableCell>
                    {record.parameters.map((param, index) => (
                      <div key={index} style={{ marginBottom: '0.25rem', whiteSpace: 'nowrap' }}>
                        {param.normal}
                      </div>
                    ))}
                  </TableCell>
                  <TableCell style={{ textAlign: 'left' }}>
                    {/* แสดง Templates */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', marginBottom: '0.5rem' }}>
                      {record.templates.map((templateName, index) => {
                        // หา matched template data ที่ตรงกับ template name นี้
                        const matchedTemplate = record.matchedTemplates?.find(
                          t => t.template_name === templateName
                        );

                        return (
                          <TemplateBadge
                            key={index}
                            onClick={() => handleTemplateClick(templateName, matchedTemplate)}
                          >
                            <TemplateBadgeText>{templateName}</TemplateBadgeText>
                            <RemoveButton
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveTemplate(record.id, index);
                              }}
                              title="ลบ template"
                            >
                              <X size={8} />
                            </RemoveButton>
                          </TemplateBadge>
                        );
                      })}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                      <div style={{ display: 'flex', gap: '0.25rem' }}>
                        <EditButton
                          onClick={() => handleEdit(record.id)}
                        >
                          แก้ไข
                        </EditButton>
                        <SendResultButton onClick={() => handleSendResult(record.id)}>
                          {record.isApproved ? 'ส่งใหม่' : 'กดเพื่อส่งผลสุขภาพ'}
                        </SendResultButton>
                      </div>
                      {record.isApproved && (
                        <HistoryButton onClick={() => handleViewHistory(record.id)}>
                          <History />
                          ประวัติการส่งผลสุขภาพ
                        </HistoryButton>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', alignItems: 'center' }}>
                      <ViewDataButton onClick={() => handleViewData(record.id)}>
                        ดูข้อมูล
                      </ViewDataButton>
                      <StatusText>สถานะจัดส่ง</StatusText>
                      <StatusBadge status={
                        record.approvalStatus === 'approved' ? 'อนุมัติและจัดส่งผลการตรวจสุขภาพแล้ว' :
                        record.approvalStatus === 'revised' ? 'มีการแก้ไขรอการอนุมัติอีกครั้ง' :
                        'รอการอนุมัติ'
                      }>
                        {record.approvalStatus === 'approved' ? 'อนุมัติและจัดส่งผลการตรวจสุขภาพแล้ว' :
                         record.approvalStatus === 'revised' ? 'มีการแก้ไขรอการอนุมัติอีกครั้ง' :
                         'รอการอนุมัติ'}
                      </StatusBadge>
                      <ActionPrintButton
                        onClick={() => handlePrint(record.id)}
                        disabled={!record.isApproved}
                      >
                        <Printer size={14} />
                        Print
                      </ActionPrintButton>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </tbody>
          </Table>
        </TableContainer>
      </PageContainer>

      <TemplateDetailModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        templateData={selectedTemplate}
      />

      <EditTemplateModal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        recordId={selectedRecordId}
        selectedRecord={selectedRecord}
        onSave={fetchHealthRecords}
      />

      <HealthResultModal
        isOpen={isHealthResultModalOpen}
        onClose={handleCloseHealthResultModal}
        selectedRecord={selectedRecord}
        onApprove={handleApprove}
      />

      <SendHealthResultModal
        isOpen={isSendHealthResultModalOpen}
        onClose={handleCloseSendHealthResultModal}
        onConfirm={handleConfirmSendAgain}
      />

      <HealthReportModal
        isOpen={isHealthReportModalOpen}
        onClose={handleCloseHealthReportModal}
        recordData={selectedRecord}
      />

      {/* Hidden Templates Modal */}
      {isHiddenTemplatesModalOpen && (
        <HiddenTemplatesModal
          isOpen={isHiddenTemplatesModalOpen}
          onClose={() => setIsHiddenTemplatesModalOpen(false)}
          hiddenTemplates={hiddenTemplates}
          onRestore={handleRestoreTemplate}
        />
      )}
    </Layout>
  );
};

// Hidden Templates Modal Component
const HiddenTemplatesModal = ({ isOpen, onClose, hiddenTemplates, onRestore }) => {
  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Templates ที่ซ่อนไว้</ModalTitle>
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </ModalHeader>
        <ModalBody>
          {hiddenTemplates.length === 0 ? (
            <EmptyMessage>ไม่มี Templates ที่ซ่อนไว้</EmptyMessage>
          ) : (
            <HiddenTemplatesList>
              {hiddenTemplates.map((template) => (
                <HiddenTemplateItem key={template.template_id}>
                  <TemplateInfo>
                    <TemplateName>{template.template_name}</TemplateName>
                    <TemplateDetails>
                      <div>ซ่อนเมื่อ: {new Date(template.hidden_at).toLocaleString('th-TH')}</div>
                      {template.tags && template.tags.length > 0 && (
                        <TagsContainer>
                          {template.tags.map((tag, index) => (
                            <Tag key={index}>{tag}</Tag>
                          ))}
                        </TagsContainer>
                      )}
                    </TemplateDetails>
                  </TemplateInfo>
                  <RestoreButton onClick={() => onRestore(template.template_id)}>
                    เอากลับมา
                  </RestoreButton>
                </HiddenTemplateItem>
              ))}
            </HiddenTemplatesList>
          )}
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  );
};

// Modal Styled Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 0.5rem;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #E5E7EB;
`;

const ModalTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #6B7280;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #111827;
  }
`;

const ModalBody = styled.div`
  padding: 1.5rem;
  overflow-y: auto;
`;

const EmptyMessage = styled.div`
  text-align: center;
  color: #6B7280;
  padding: 2rem;
`;

const HiddenTemplatesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const HiddenTemplateItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border: 1px solid #E5E7EB;
  border-radius: 0.375rem;
  background-color: #F9FAFB;
`;

const TemplateInfo = styled.div`
  flex: 1;
`;

const TemplateName = styled.div`
  font-weight: 600;
  color: #111827;
  margin-bottom: 0.5rem;
`;

const TemplateDetails = styled.div`
  font-size: 0.875rem;
  color: #6B7280;
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin-top: 0.5rem;
`;

const Tag = styled.span`
  background-color: #E0F2F1;
  color: #00796B;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
`;

const RestoreButton = styled.button`
  background-color: #00B5B8;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #009FA2;
  }
`;

export default HealthBook;